import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../design-system/components';

/**
 * Privacy Policy and Terms of Use. These describe how KidzIM actually behaves:
 * kids have no email or personal details beyond a screen name, chats are
 * supervised and stored for the supervising grown-up, moderation is enforced
 * server-side, and secrets are hashed — never stored or logged in the clear.
 * Keep this copy in step with what the product does; if behavior changes, the
 * relevant section here changes too.
 */

const LAST_UPDATED = 'June 24, 2026';
const CONTACT = 'privacy@kidzim.com';
// COPPA requires the operator's legal name and a physical mailing address in the
// direct notice and the privacy policy. Fill these in before launch.
const OPERATOR_NAME = '[Legal entity name]';
const OPERATOR_ADDRESS = '[Mailing address]';

interface Section {
  heading: string;
  body: ReactNode;
}

function P({ children }: { children: ReactNode }) {
  return (
    <p className="kz-muted" style={{ marginBottom: 'var(--space-3)', lineHeight: 1.6 }}>
      {children}
    </p>
  );
}

function UL({ items }: { items: ReactNode[] }) {
  return (
    <ul className="kz-muted" style={{ margin: '0 0 var(--space-3) var(--space-5)', lineHeight: 1.6 }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 'var(--space-1)' }}>
          {it}
        </li>
      ))}
    </ul>
  );
}

const privacySections: Section[] = [
  {
    heading: 'Who this is for',
    body: (
      <>
        <P>
          KidzIM is a safe, supervised chat service for kids, set up and overseen by a parent or guardian.
          A grown-up creates the account, adds each child, and supervises their chats. We designed KidzIM
          around a simple rule: <strong>collect as little as possible, and keep children’s information to a minimum.</strong>
        </P>
        <P>
          KidzIM is operated by {OPERATOR_NAME}, {OPERATOR_ADDRESS}. This policy explains what we collect, why,
          and the choices you have. If anything here is unclear, email{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and we’ll help.
        </P>
      </>
    ),
  },
  {
    heading: 'Children’s privacy & COPPA',
    body: (
      <>
        <P>
          KidzIM is intended for children and is covered by the U.S. Children’s Online Privacy Protection Act
          (COPPA) and the FTC’s COPPA Rule. We take that seriously: a child only ever reaches KidzIM through an
          account a parent or guardian creates and controls, and we collect the bare minimum needed to run a
          supervised chat.
        </P>
        <P>
          <strong>The only information we collect from a child is a screen name and a 6-digit PIN</strong> (the
          PIN is stored hashed). We do <strong>not</strong> ask a child for an email address, real name, phone
          number, home address, photo, full date of birth, location, biometric or government identifiers, or any
          persistent advertising identifier. A parent may optionally record a child’s <strong>birth year only</strong>{' '}
          (never a full birthday) to set age-appropriate defaults; this is entered by the grown-up, not the child.
        </P>
        <P>
          We never condition a child’s participation on disclosing more information than is reasonably necessary
          to take part in the chat, and we don’t use children’s information for behavioral advertising or sell it
          to anyone. We retain a child’s information only as long as needed for the service (see{' '}
          <em>How long we keep things</em>) and delete it on request.
        </P>
      </>
    ),
  },
  {
    heading: 'How a grown-up gives consent',
    body: (
      <>
        <P>
          Before a child uses KidzIM, COPPA requires us to give the parent a direct notice and obtain{' '}
          <strong>verifiable parental consent</strong>. We use the FTC-recognized <strong>“email-plus”</strong>{' '}
          method, which is permitted here precisely because we never use or disclose a child’s information for
          third-party advertising:
        </P>
        <UL
          items={[
            'A grown-up creates the account with their own email and password and confirms they are the child’s parent or legal guardian (or have that grown-up’s permission).',
            'We send a confirmation to that email describing what we collect from children and how it’s used — this notice — and ask the grown-up to confirm consent.',
            'We follow up with a second confirmation notice after a short delay, so the parent can decline or withdraw consent before or after a child starts chatting.',
            'We keep a dated record that consent was given, and by whom.',
          ]}
        />
        <P>
          Consenting to collection is separate from consenting to any sharing. Because{' '}
          <strong>we don’t disclose children’s information to third parties for advertising</strong>, there is
          nothing of that kind to opt into. You can withdraw consent at any time by deleting your child’s profile
          or your account, or by emailing <a href={`mailto:${CONTACT}`}>{CONTACT}</a>; we will then stop
          collecting from that child and delete their information.
        </P>
      </>
    ),
  },
  {
    heading: 'What we collect',
    body: (
      <>
        <P>
          <strong>From grown-ups (the account holder and any co-parents or trusted adults):</strong>
        </P>
        <UL
          items={[
            'Email address and a display name, so you can sign in and we can reach you about your account.',
            'A password, which we store only as a salted hash (argon2id) — never in plain text.',
            'Optional invitee emails you choose to enter when inviting another child’s grown-up to a room.',
          ]}
        />
        <P>
          <strong>From kids:</strong> only a <strong>screen name</strong> and a 6-digit PIN (stored hashed).
          Kids have <strong>no email address</strong> and we don’t ask them for their real name, phone number,
          address, photos, birthday, or any other personal detail. A child signs in with the family code,
          their screen name, and their PIN.
        </P>
        <P>
          <strong>Chat content:</strong> messages sent in a chat room, or in a 1:1 <strong>buddy chat</strong>{' '}
          between two kids, are stored so the supervising grown-up (or grown-ups) can read them. We also keep
          basic operational records (e.g. when a room or buddy chat opened or closed, and a security audit log
          of account actions) to keep the service safe and working.
        </P>
      </>
    ),
  },
  {
    heading: 'What we don’t do',
    body: (
      <UL
        items={[
          'We don’t sell or rent your or your child’s information to anyone.',
          'We don’t show third-party advertising to kids, and we don’t build advertising profiles.',
          'We don’t ask children for personal information beyond a screen name.',
          'We don’t log secrets (passwords, PINs, tokens) or message bodies in our server logs — sensitive fields are redacted.',
        ]}
      />
    ),
  },
  {
    heading: 'How chats are kept safe',
    body: (
      <>
        <P>
          Every message — in a chat room or a 1:1 buddy chat — passes through server-side moderation that can’t
          be worked around by the app. Per your settings, links and images can be blocked, and phone numbers and
          email addresses are <strong>always</strong> redacted and flagged, regardless of settings — including
          common attempts to disguise them. Enforcement happens on our servers, not in the child’s app.
        </P>
        <P>
          Chats are supervised by design. In a chat room, the grown-up who runs it can read its full transcript
          at any time. <strong>Buddy chat</strong> (an optional, 1:1 line between two accepted buddies, off by
          default until a parent turns it on) works the same way, except a buddy chat's transcript is visible to
          the supervising grown-up(s) of <strong>both</strong> kids in the pair, not just one family — since it's
          a conversation between two children from two different households, both sides supervise it. We frame
          this as care, not surveillance — it’s how we keep kids safe.
        </P>
      </>
    ),
  },
  {
    heading: 'How we use information',
    body: (
      <UL
        items={[
          'To provide the service: sign-in, family and room management, and delivering chat messages.',
          'To keep it safe: moderation, abuse prevention, rate limiting, and a security audit log.',
          'To communicate with grown-ups: account-related email such as room invites (we use a third-party email provider, currently SendGrid, only to deliver these messages).',
        ]}
      />
    ),
  },
  {
    heading: 'Who we share with',
    body: (
      <>
        <P>
          We don’t sell or rent anyone’s information, and we don’t share children’s information with third parties
          for advertising. We share information only with the service providers we need to run KidzIM, and only so
          they can perform work for us under contract:
        </P>
        <UL
          items={[
            'Our hosting and database providers, which store the account, family, and chat data on our behalf.',
            'Our email provider (currently Twilio SendGrid), used only to deliver account-related email such as room invites to grown-ups — never to message children.',
          ]}
        />
        <P>
          These providers may process data only on our instructions. We may also disclose information if the law
          requires it, or to protect the safety of a child or others — for example, responding to a valid legal
          request or a credible safety concern.
        </P>
      </>
    ),
  },
  {
    heading: 'How long we keep things',
    body: (
      <>
        <P>
          COPPA asks us to keep children’s information only as long as is reasonably necessary, and to say so. Our
          retention policy:
        </P>
        <UL
          items={[
            'Chat messages are kept so the supervising grown-up can read the transcript, and until the grown-up clears the transcript, deletes the room, or deletes the child or the account.',
            'A child’s profile (screen name, hashed PIN, optional birth year) is kept while the account is active and that child exists in the family.',
            'Security and audit records are kept for a limited period to keep the service safe and to investigate abuse, then aged out.',
            'When information is no longer needed for these purposes, we delete it.',
          ]}
        />
        <P>
          You’re in control: a grown-up can delete a child’s profile, clear a room’s transcript, or delete the
          whole account from account settings. Deleting a child’s profile or clearing a transcript takes effect
          right away. When you delete your account we begin a <strong>30-day grace period</strong> and then
          permanently purge the account and its data; you can cancel during those 30 days simply by signing back in.
        </P>
      </>
    ),
  },
  {
    heading: 'Security',
    body: (
      <P>
        Passwords, PINs, and invite/refresh tokens are hashed before storage and never logged in the clear.
        Connections are encrypted in transit. No system is perfect, but security comes first here — there are
        children involved — and we make the safer, more private choice when there’s a trade-off.
      </P>
    ),
  },
  {
    heading: 'Your rights as a parent',
    body: (
      <>
        <P>
          Because children interact with KidzIM only through an account a parent or guardian creates and
          supervises, that grown-up exercises children’s rights on their behalf. Under COPPA, as the grown-up
          who set up the account you can, at any time:
        </P>
        <UL
          items={[
            'Review the personal information we’ve collected from your child.',
            'Correct it, or have us delete it.',
            'Refuse to allow any further collection or use of your child’s information — which you can do by deleting the child’s profile or your account.',
          ]}
        />
        <P>
          Most of this is self-service from account settings; you can also email{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. To protect children, we’ll confirm you’re the account’s
          grown-up before acting on a request.
        </P>
      </>
    ),
  },
  {
    heading: 'Your U.S. state privacy rights',
    body: (
      <>
        <P>
          Depending on where you live (for example California, and other states with comprehensive privacy
          laws), you may have additional rights over the personal information in your account: the right to know
          what we collect, to access or correct it, to delete it, and to opt out of any “sale” or “sharing” of
          personal information or targeted advertising.
        </P>
        <P>
          For KidzIM the last one is simple: <strong>we do not sell or share personal information, and we do not
          use it for targeted advertising</strong> — for grown-ups or for kids — so there is nothing to opt out
          of. We also don’t use the limited information we hold to make decisions that produce legal or similarly
          significant effects. To exercise any of these rights, use account settings or email{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>; we won’t discriminate against you for doing so.
        </P>
      </>
    ),
  },
  {
    heading: 'How to reach us',
    body: (
      <P>
        Questions, requests, or concerns about privacy — including anything about your child’s information — can
        go to <a href={`mailto:${CONTACT}`}>{CONTACT}</a>, or by mail to {OPERATOR_NAME}, {OPERATOR_ADDRESS}. If
        you believe we’ve collected information from a child without the proper grown-up’s consent, contact us and
        we’ll delete it promptly. You may also contact the U.S. Federal Trade Commission at{' '}
        <a href="https://www.ftc.gov" target="_blank" rel="noreferrer">ftc.gov</a>.
      </P>
    ),
  },
  {
    heading: 'Changes to this policy',
    body: (
      <P>
        If we make a meaningful change, we’ll update the date above and, when appropriate, let account holders
        know by email. Continuing to use KidzIM after a change means you accept the updated policy.
      </P>
    ),
  },
];

const termsSections: Section[] = [
  {
    heading: 'Accepting these terms',
    body: (
      <P>
        By creating a KidzIM account or using the service, you agree to these Terms of Use. You must be an
        adult (18 or older) to create an account, and you must have the authority to set up and supervise any
        child you add. KidzIM is currently free while in beta.
      </P>
    ),
  },
  {
    heading: 'Your account and your kids',
    body: (
      <>
        <P>
          You’re responsible for everything that happens under your account, including the children you add and
          any co-parents or trusted adults you invite. Keep your password private, and choose grown-ups you
          trust when sharing access to a family or room.
        </P>
        <P>
          You confirm that you are the parent or legal guardian of the children you add, or that you have
          permission from their parent or guardian to set them up and supervise their chats.
        </P>
      </>
    ),
  },
  {
    heading: 'Acceptable use',
    body: (
      <>
        <P>KidzIM is for friendly, supervised chat between kids. You and your family agree not to:</P>
        <UL
          items={[
            'Use KidzIM to harass, bully, threaten, or harm anyone.',
            'Share illegal, hateful, sexual, or otherwise harmful content.',
            'Try to defeat or evade moderation, share contact details to move kids off-platform, or attempt to contact children you don’t have a relationship with.',
            'Attempt to break, overload, reverse-engineer, or gain unauthorized access to the service.',
            'Impersonate someone else or misrepresent who you are.',
          ]}
        />
        <P>
          We may suspend or remove accounts that break these rules to protect kids and the community.
        </P>
      </>
    ),
  },
  {
    heading: 'Moderation & supervision',
    body: (
      <P>
        Chats — chat rooms and 1:1 buddy chats alike — are moderated on our servers. A room is supervised by the
        grown-up who runs it; a buddy chat is supervised by the managing grown-up(s) of <strong>both</strong>{' '}
        kids in the pair, each of whom can read its full transcript. Moderation is a safety layer, not a
        guarantee — supervising grown-ups remain responsible for the children in their care. If you see
        something concerning, contact us.
      </P>
    ),
  },
  {
    heading: 'Your content',
    body: (
      <P>
        Your family keeps ownership of the messages you send. You grant KidzIM the limited permission needed to
        store and display that content to the right people (for example, showing a transcript to the supervising
        grown-up) and to moderate it for safety. We don’t use your content to advertise to you or your kids.
      </P>
    ),
  },
  {
    heading: 'Service availability',
    body: (
      <P>
        KidzIM is provided “as is,” and during beta features may change, break, or be removed. We don’t promise
        uninterrupted service. We’ll do our best to keep things running and to give notice of significant
        changes, but to the extent the law allows, KidzIM isn’t liable for indirect or incidental damages
        arising from use of the service.
      </P>
    ),
  },
  {
    heading: 'Ending your use',
    body: (
      <P>
        You can stop using KidzIM and delete your account at any time from account settings (a 30-day grace
        period applies before permanent deletion — see our{' '}
        <Link to="/privacy">Privacy Policy</Link>). We may suspend or end access for accounts that violate these
        terms or put children at risk.
      </P>
    ),
  },
  {
    heading: 'Changes & contact',
    body: (
      <P>
        We may update these terms; we’ll revise the date above and, when it matters, notify account holders.
        Questions? Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </P>
    ),
  },
];

export function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
  const isPrivacy = kind === 'privacy';
  const title = isPrivacy ? 'Privacy Policy' : 'Terms of Use';
  const sections = isPrivacy ? privacySections : termsSections;
  const intro = isPrivacy
    ? 'KidzIM is built to keep kids safe and to collect as little data as possible. Here’s exactly what that means.'
    : 'The ground rules for using KidzIM — written to keep the service safe, simple, and fair for families.';

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 'var(--space-12) var(--space-6)' }}>
      <Link to="/" aria-label="KidzIM home">
        <Logo height={30} />
      </Link>

      <h1 style={{ font: 'var(--type-h1)', margin: 'var(--space-8) 0 var(--space-2)' }}>{title}</h1>
      <p className="kz-muted" style={{ marginBottom: 'var(--space-2)' }}>{intro}</p>
      <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-8)' }}>
        Last updated: {LAST_UPDATED}
      </p>

      {sections.map((s) => (
        <section key={s.heading} style={{ marginBottom: 'var(--space-6)' }}>
          <h2 style={{ font: 'var(--type-h3)', margin: '0 0 var(--space-2)' }}>{s.heading}</h2>
          {s.body}
        </section>
      ))}

      <p className="kz-muted" style={{ font: 'var(--type-small)', marginTop: 'var(--space-8)' }}>
        See also our{' '}
        {isPrivacy ? <Link to="/terms">Terms of Use</Link> : <Link to="/privacy">Privacy Policy</Link>}.
      </p>
      <p style={{ marginTop: 'var(--space-4)' }}>
        <Link to="/">← Back to home</Link>
      </p>
    </div>
  );
}
